import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { usePitch } from "../context/PitchContext";
import { Logo } from "./Logo";

const ease = [0.22, 1, 0.36, 1];

const Nav = () => (
  <header className="absolute inset-x-0 top-0 z-20">
    <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-6 md:px-10">
      <Logo light />
      <a href="#talk" data-testid="header-contact-btn" className="rounded-full border border-[#F4EDE3]/30 px-5 py-2.5 text-sm text-[#F4EDE3] transition-colors duration-300 hover:bg-[#C87D55] hover:border-[#C87D55]">
        Let's talk
      </a>
    </div>
  </header>
);

export const Hero = () => {
  const pitch = usePitch();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0.2]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative flex min-h-[100svh] flex-col overflow-hidden bg-[#0B192C] text-[#F4EDE3]">
      <motion.div style={{ y }} className="absolute inset-0 lg:left-auto lg:w-[44%] lg:overflow-hidden">
        <motion.img
          src={pitch.heroImage}
          alt={pitch.heroImageAlt}
          initial={{ scale: 1.25, filter: "brightness(0.3)" }}
          animate={{ scale: 1.05, filter: "brightness(1)" }}
          transition={{ duration: 3.2, ease }}
          className="h-full w-full object-cover object-[45%_center] lg:object-[center_55%]"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C] via-[#0B192C]/40 to-[#0B192C]/30 lg:hidden" />
      <div className="absolute inset-y-0 right-0 hidden w-[44%] bg-gradient-to-r from-[#0B192C] via-[#0B192C]/10 to-transparent lg:block" />
      <div className="absolute inset-x-0 bottom-0 hidden h-40 bg-gradient-to-t from-[#0B192C] to-transparent lg:block" />
      <div className="grain absolute inset-0" />
      <Nav />
      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-end px-6 pb-14 pt-32 md:px-10 md:pb-20">
        <div className="lg:w-[56%]">
          <motion.p
            data-testid="hero-vessel-badge"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 border-l-2 border-[#C87D55] bg-[#0B192C]/70 py-1.5 pl-4 pr-3 font-mono text-xs uppercase tracking-[0.22em] text-[#D4A373] backdrop-blur-md lg:bg-transparent lg:backdrop-blur-none"
          >
            <span>For {pitch.customerName}</span>
            <span className="text-[#F4EDE3]/50">/</span>
            <span>{pitch.vesselName}</span>
          </motion.p>
          <h1 data-testid="hero-headline" className="mt-6 font-display text-[clamp(2.6rem,10.5vw,5.75rem)] leading-[0.95] tracking-tight">
            {pitch.headlineLines.map((line, i) => (
              <span key={`${i}-${line}`} className="block overflow-hidden pb-[0.08em]">
                <motion.span
                  className={`block ${i === 1 ? "italic text-[#D4A373]" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1.2, delay: 0.5 + i * 0.16, ease }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            data-testid="hero-supporting-line"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease }}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-200 md:text-xl"
          >
            MarineOS is the one place where <span className="text-[#D4A373]">{pitch.vesselName}</span>'s operations, certificates, maintenance and compliance live.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="mt-10 flex flex-col gap-6 border-t border-[#F4EDE3]/20 pt-6 md:flex-row md:items-center md:justify-between"
          >
            <p data-testid="hero-vessel-story" className="max-w-md font-display text-lg italic text-[#F4EDE3]/90">{pitch.vesselStory}</p>
            <a href="#screen" data-testid="hero-scroll-cue" className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-[#D4A373]">
              See it in action <ArrowDown className="h-4 w-4 animate-bounce" />
            </a>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
